import {ModuleStringTypes} from './constants';
import type NetatmoWeatherModule from './NetatmoWeatherModule';
import type NetatmoWeatherStation from './NetatmoWeatherStation';

/**
 * Which Netatmo modules and metrics an alert condition may be set on.
 *
 * Mirrors the backend gate, `is_owner_private_condition` in
 * apps/alerts/metric_access.py: on a station the user does not own (a Netatmo
 * favourite, or a station shared read-only), only the outdoor modules
 * (NAModule1-3) and the base station's pressure are public. Anything else —
 * the base station's indoor measures, every NAModule4 — is refused with a
 * generic 400, so it is not offered.
 *
 * The backend also runs its private-key check on the outdoor modules' metrics;
 * none of the metrics an outdoor module maps to (T, U, FF, FXY, RR_*) is a
 * private key, so there is nothing to mirror there.
 */

const PUBLIC_MODULE_TYPES: readonly string[] = [
  ModuleStringTypes.NA_MODULE1,
  ModuleStringTypes.NA_MODULE2,
  ModuleStringTypes.NA_MODULE3,
];

const PUBLIC_BASE_STATION_METRICS: readonly string[] = ['SLP'];

// `isOwner` is resolved by the station's constructor (getIsOwner). Anything
// else — including the `{}` EditAlertScreen passes for a station that has left
// the user's list — fails closed.
const isOwner = (station: NetatmoWeatherStation | undefined): boolean =>
  station?.isOwner === true;

export const getAlertableMetrics = (
  station: NetatmoWeatherStation | undefined,
  module: NetatmoWeatherModule,
): string[] => {
  const metrics: string[] = module.getAvailableMetrics();
  if (isOwner(station)) {
    return metrics;
  }
  const type = module.getType();
  if (PUBLIC_MODULE_TYPES.includes(type)) {
    return metrics;
  }
  if (type === ModuleStringTypes.NA_MAIN) {
    return metrics.filter((m) => PUBLIC_BASE_STATION_METRICS.includes(m));
  }
  return [];
};

/**
 * The station's modules an alert may be set on. An owner gets the full module
 * list, unchanged; otherwise a module is listed only if it has at least one
 * alertable metric.
 */
export const getAlertableModules = (
  station: NetatmoWeatherStation | undefined,
): NetatmoWeatherModule[] => {
  const moduleList = station?.moduleList ?? [];
  if (isOwner(station)) {
    return moduleList;
  }
  return moduleList.filter(
    (module) => getAlertableMetrics(station, module).length > 0,
  );
};
